import { useState } from 'react'

import cn from 'classnames'

import { FAQProps } from './FAQ.types'

export const FAQ = (props: FAQProps) => {
    const { className } = props
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    const faqs = [
        {
            question: 'What is Nexly?',
            answer: 'Nexly is a financial statement review platform designed for audit, accounting, and financial reporting teams. It automates the review process through advanced error detection, automatic PDF annotations, and helps users navigate financial reporting standards.',
        },
        {
            question: 'How secure is Nexly?',
            answer: 'Nexly uses industry-standard protocols to protect your data. Key features include HTTPS, AES-256 bit encryption (bank-grade), SSL, and stateful firewall protection. All data is encrypted at rest and in transit. We enforce strict access controls and continuous monitoring to prevent unauthorized access.',
        },
        {
            question: 'Who are your clients?',
            answer: 'Nexly is trusted by audit, accounting, and financial reporting teams across the globe, including leading audit and financial services firms. Our platform has been supporting professionals for years in streamlining their financial statement review processes.',
        },
        {
            question: 'What are the benefits of using Nexly?',
            answer: 'Nexly significantly reduces the time spent on financial statement reviews, minimizes the risk of undetected errors, and improves collaboration among reviewers and stakeholders involved in preparing financial statements. It transforms a tedious process into a streamlined, intelligent workflow.',
        },
        {
            question: 'Do financial statements need to follow a specific template?',
            answer: 'No. Nexly is template-agnostic and works with any format of annual or periodic financial reports.',
        },
        {
            question: 'What is your pricing model?',
            answer: 'We charge a fixed annual fee per entity. The fee remains the same regardless of how many PDF drafts are reviewed during the reporting period. Schedule a demo to receive a tailored quote for your firm.',
        },
        {
            question: 'Can Nexly access the data?',
            answer: 'Access to your data is strictly limited and controlled. Nexly staff do not access your data unless explicitly authorized by you for support or troubleshooting purposes, and even then, under strict confidentiality and security protocols.',
        },
        {
            question: 'Where do you store the data?',
            answer: 'By default, all data is securely stored in the European Union, specifically in Germany. Upon request, data can be stored in an alternative location specified by the client.',
        },
    ]

    const toggleItem = (index: number) => {
        setOpenIndex(openIndex === index ? null : index)
    }

    return (
        <section className={cn('px-4 py-16 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8 lg:py-20 bg-white', className)}>
            <div className='max-w-xl sm:mx-auto lg:max-w-2xl'>
                <div className='max-w-xl mb-10 md:mx-auto sm:text-center lg:max-w-2xl md:mb-12'>
                    <div>
                        <p className='inline-block px-3 py-px mb-4 text-xs font-semibold tracking-wider text-teal-900 uppercase rounded-full bg-teal-accent-400'>
                            FAQ
                        </p>
                    </div>
                    <h2 className='max-w-lg mb-6 font-sans text-3xl font-bold leading-none tracking-tight text-gray-900 sm:text-4xl md:mx-auto'>
                        Frequently Asked Questions
                    </h2>
                    <p className='text-base text-gray-700 md:text-lg'>
                        Find answers to common questions about Nexly and how it can help streamline your financial statement review process.
                    </p>
                </div>
                <div className='space-y-4'>
                    {faqs.map((faq, index) => (
                        <div key={faq.question} className='border-b'>
                            <button
                                type='button'
                                aria-label='Open item'
                                title='Open item'
                                onClick={() => { toggleItem(index) }}
                                className='flex items-center justify-between w-full p-4 focus:outline-none'
                            >
                                <p className='text-lg font-medium'>{faq.question}</p>
                                <svg
                                    viewBox='0 0 24 24'
                                    className={cn(
                                        'w-3 text-gray-600 transition-transform duration-200',
                                        openIndex === index && 'transform rotate-180',
                                    )}
                                >
                                    <polyline
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeMiterlimit='10'
                                        points='2,7 12,17 22,7'
                                        strokeLinejoin='round'
                                    />
                                </svg>
                            </button>
                            {openIndex === index && (
                                <div className='p-4 pt-0'>
                                    <p className='text-gray-700'>{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}
